import React from 'react';
import { Filter } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { MONTHS } from '../../utils/helpers';

/**
 * Panel de filtros del Dashboard
 */
const FilterPanel = ({
  // Estado de filtros
  destinationFilter,
  onDestinationFilterChange,
  filters,
  onFiltersChange,
  searchField,
  onSearchFieldChange,
  searchValue,
  onSearchValueChange,
  // Datos
  destinations,
  estados,
  availableYears,
  availableSearchFields,
  // Permisos
  isRestrictedUser,
  restrictedDestination,
  // Contadores
  filteredCount,
  totalCount,
  // Acciones
  onClearFilters,
}) => {
  // Get label for current search field
  const getSearchFieldLabel = () => {
    const field = availableSearchFields.find(f => f.value === searchField);
    return field ? field.label : 'Buscar';
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Filter size={18} />
          Filtros
        </CardTitle>
      </CardHeader>
      <CardContent className="pb-4">
        {/* Destination filter - Fixed */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3 pb-3 border-b">
          {/* Destino */}
          <div className="space-y-1">
            <Label className="text-xs font-semibold text-blue-700">
              Destino
              {isRestrictedUser && (
                <span className="ml-2 text-xs text-gray-500 font-normal">(Asignado)</span>
              )}
            </Label>
            <Select
              value={destinationFilter}
              onValueChange={onDestinationFilterChange}
              data-testid="filter-destination"
              disabled={isRestrictedUser}
            >
              <SelectTrigger className={`h-9 border-blue-300 ${isRestrictedUser ? 'bg-gray-100 cursor-not-allowed' : 'bg-blue-50'}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {!isRestrictedUser && <SelectItem value="all">Todos los destinos</SelectItem>}
                {destinations
                  .filter(dest => !isRestrictedUser || dest.name === restrictedDestination)
                  .map((dest) => (
                    <SelectItem key={dest.id} value={dest.name}>
                      {dest.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          {/* Año */}
          <div className="space-y-1">
            <Label className="text-xs">Año</Label>
            <Select
              value={filters.year}
              onValueChange={(value) => onFiltersChange({ ...filters, year: value })}
              data-testid="filter-year"
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los años</SelectItem>
                {availableYears.map((year) => (
                  <SelectItem key={year} value={year}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Mes */}
          <div className="space-y-1">
            <Label className="text-xs">Mes</Label>
            <Select
              value={filters.month}
              onValueChange={(value) => onFiltersChange({ ...filters, month: value })}
              data-testid="filter-month"
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los meses</SelectItem>
                {MONTHS.map((month) => (
                  <SelectItem key={month.value} value={month.value}>
                    {month.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Buscar por */}
          <div className="space-y-1">
            <Label className="text-xs">Buscar por:</Label>
            <Select
              value={searchField}
              onValueChange={(value) => {
                onSearchFieldChange(value);
                onSearchValueChange(''); // Reset search value when changing field
              }}
              disabled={destinationFilter === 'all'}
              data-testid="search-field-select"
            >
              <SelectTrigger className={`h-9 ${destinationFilter === 'all' ? 'bg-gray-100 cursor-not-allowed' : ''}`}>
                <SelectValue placeholder={destinationFilter === 'all' ? 'Selecciona destino primero' : 'Selecciona campo...'} />
              </SelectTrigger>
              <SelectContent>
                {availableSearchFields.map((field) => (
                  <SelectItem key={field.value} value={field.value}>
                    {field.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Buscar (Input o Select según campo) */}
          <div className="space-y-1">
            <Label className="text-xs">Buscar:</Label>
            {searchField === 'status_id' ? (
              <Select
                value={searchValue}
                onValueChange={onSearchValueChange}
                disabled={destinationFilter === 'all' || !searchField}
                data-testid="search-value-select"
              >
                <SelectTrigger className={`h-9 ${(destinationFilter === 'all' || !searchField) ? 'bg-gray-100 cursor-not-allowed' : ''}`}>
                  <SelectValue placeholder="Selecciona un estado..." />
                </SelectTrigger>
                <SelectContent>
                  {estados
                    .filter(est => est.destinations && est.destinations.includes(destinationFilter))
                    .map((est) => (
                      <SelectItem key={est.id} value={est.id}>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: est.color }}
                          />
                          {est.name}
                        </div>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                type="text"
                placeholder={
                  destinationFilter === 'all' 
                    ? 'Selecciona destino primero' 
                    : (searchField ? `Buscar ${getSearchFieldLabel()}...` : 'Selecciona campo...')
                }
                value={searchValue}
                onChange={(e) => onSearchValueChange(e.target.value)}
                disabled={destinationFilter === 'all' || !searchField}
                data-testid="search-input"
                className={`h-9 ${(destinationFilter === 'all' || !searchField) ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              />
            )}
          </div>
        </div>

        {/* Footer con contador y botón limpiar */}
        <div className="mt-3 pt-3 border-t flex justify-between items-center">
          <p className="text-sm text-gray-600">
            Mostrando {filteredCount} de {totalCount} registros
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={onClearFilters}
            data-testid="clear-filters"
          >
            Limpiar Filtros
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default FilterPanel;
